// What the service worker does, written so it can be tested without one.
//
// `src/sw.ts` IS the worker: it is bundled on its own by `injectManifest`,
// runs against `ServiceWorkerGlobalScope`, and can only be exercised by a
// browser. Everything in it that involves a decision lives here instead, as
// functions that take what they need — the registration's showNotification,
// the push subscription, the window clients, a `post` — as arguments. The
// worker hands in the real ones; the tests hand in fakes. Nothing here may
// import workbox or touch `self`, or it stops being loadable in node.
//
// Ported from the Andon app's `sw.ts`, where the same decisions were inline.
// See each function for why it does what it does; the worker file says why
// the worker exists at all.

export interface PushPayload {
  title: string;
  body: string;
  alertId: number;
  stars: number;
  tag: string;
}

/**
 * Navigations the shell must NOT answer.
 *
 * The API is never cached — a sign-off only counts when the server confirms
 * it. The ISW media paths are denied too, and that is not belt-and-braces: an
 * <iframe> load is a NAVIGATION request, so without this the worker answers a
 * card with the precached app shell and the operator gets the whole app
 * rendered inside the card frame. The library screen itself stays allowed —
 * it is a real SPA route and should still open from the shell offline — so
 * this denies the media subpaths, not /isw. /drawings for the same reason: a
 * drawing opens as a page of its own, and answered with the app shell it would
 * open the app a second time in a new tab instead of the PDF.
 *
 * The same three the generateSW config carried, verbatim: the custom worker
 * must not be worse at this than the generated one was.
 */
export const NAVIGATION_DENYLIST: RegExp[] = [/^\/api/, /^\/isw\/(cards|videos)\//, /^\/drawings\//];

/** Whether a navigation to this path is the app's to answer with its shell. */
export function isShellNavigation(pathname: string): boolean {
  return !NAVIGATION_DENYLIST.some((re) => re.test(pathname));
}

/**
 * The headers the worker sends the API, the same ones the app sends.
 *
 * `X-SF-Api` is the CSRF guard's custom-header requirement and the JSON
 * content-type is the other half of it; cookies ride along because everything
 * here is same-origin. A worker without both gets a 400 it cannot see.
 */
export function apiHeaders(json: boolean): Record<string, string> {
  return {
    Accept: 'application/json',
    'X-SF-Api': '1',
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  };
}

/** A positive integer, or nothing. The rule every alert id in the app follows. */
export function alertIdOf(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isSafeInteger(raw) && raw > 0 ? raw : null;
}

/**
 * Read a push's payload, or refuse it.
 *
 * A push with no body, or one this build does not understand, is dropped
 * rather than shown as a blank notification: `userVisibleOnly` obliges a
 * visible notification per push, but a notification with nothing in it is
 * worse than the subscription revocation it avoids.
 */
export function parsePushPayload(raw: unknown): PushPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (typeof p['title'] !== 'string' || typeof p['body'] !== 'string' || typeof p['tag'] !== 'string') return null;
  return {
    title: p['title'],
    body: p['body'],
    // The test push is sent with an id of 0 on purpose: it names no andon.
    alertId: typeof p['alertId'] === 'number' ? p['alertId'] : 0,
    stars: typeof p['stars'] === 'number' ? p['stars'] : 1,
    tag: p['tag'],
  };
}

/** The app icon, composited as-is: the same mark that is on the home screen. */
export const NOTIFICATION_ICON = '/brand/pwa-192.png';

/**
 * NOT the app icon, and that is the fix rather than an inconsistency.
 *
 * Android does not draw the badge; it draws the badge's ALPHA CHANNEL, tinted.
 * `pwa-192.png` is the mark on a fully opaque square, so its alpha channel is a
 * rectangle — and what appeared in the status bar was a featureless grey block
 * beside an app whose launcher icon is unmistakably a mark. It read as some
 * other app's notification. `notification-badge-96.png` is the same mark with
 * a transparent field, so the silhouette Android extracts IS the mark.
 */
export const NOTIFICATION_BADGE = '/brand/notification-badge-96.png';

/** What to show for a push. */
export function notificationOptions(payload: PushPayload): NotificationOptions & { renotify: boolean } {
  return {
    body: payload.body,
    // One notification per alert: a re-notify replaces rather than stacks, so
    // a phone in a pocket does not accumulate five copies of one stopped line.
    tag: payload.tag,
    // Honoured by Chromium — where these phones live — but not in lib.dom's
    // NotificationOptions, so it is widened here rather than dropped.
    renotify: true,
    // A stopped line is worth a buzz. Anything less is not.
    requireInteraction: payload.stars === 3,
    data: { alertId: payload.alertId },
    icon: NOTIFICATION_ICON,
    badge: NOTIFICATION_BADGE,
  };
}

/** The address a notification opens: the andon's own, or the front door. */
export function alertPath(alertId: number | null): string {
  return alertId != null ? `/alert/${alertId}` : '/';
}

export interface PushDeps {
  showNotification: (title: string, options: NotificationOptions) => Promise<void>;
  getSubscription: () => Promise<{ endpoint: string } | null>;
  /** POST JSON to the API; null when the network refused. Never throws. */
  post: (path: string, body: unknown) => Promise<unknown>;
}

/**
 * Show the push, then SAY THAT IT ARRIVED.
 *
 * A 201 from the push service means Google or Apple took custody of the
 * message, and nothing more — Web Push has no delivery receipt at all. So
 * every interesting failure lives past that point and is invisible from the
 * server: notifications switched off for the browser at OS level, a Focus
 * mode, a battery optimisation that froze the service, a TTL that expired
 * while the phone was in a locker. In all of those the ack never runs, and
 * its absence is the only signal there is.
 *
 * AFTER showNotification, not before, and not instead: `userVisibleOnly`
 * obliges a visible notification for every push, and a browser that decides
 * one was not shown revokes the subscription. Diagnostics do not get to
 * jeopardise that.
 *
 * It does NOT prove a human saw it — the worker runs and draws even when the
 * OS shows nothing — which is exactly why the server reports it rather than
 * acting on it. The tag goes with it: a stopped line that is never confirmed
 * as arriving gets sent over Slack or SMS after five minutes, and without the
 * tag any unrelated delivery would cancel that.
 */
export async function handlePush(payload: PushPayload, deps: PushDeps): Promise<void> {
  await deps.showNotification(payload.title, notificationOptions(payload));
  const sub = await deps.getSubscription();
  if (sub) await deps.post('/api/andon/push/ack', { endpoint: sub.endpoint, tag: payload.tag });
}

export interface WindowClientLike {
  focus?: () => Promise<unknown>;
  postMessage: (message: unknown) => void;
}

export interface ClientsLike {
  matchAll: (options: { type: 'window'; includeUncontrolled: boolean }) => Promise<readonly WindowClientLike[]>;
  openWindow: (url: string) => Promise<unknown>;
}

/**
 * A tapped notification: reuse an open tab, or open the alert's address.
 *
 * Reuse first, rather than piling up windows on a phone: the open tab is
 * told which alert to show and brought to the front. With no tab open — on a
 * phone in a pocket the ordinary case — the id has to survive into a cold
 * start, so the worker opens the alert's own URL. That is the same address
 * somebody could paste into a handover note, and the app lands on it the same
 * way (see `lib/route`).
 */
export async function handleNotificationClick(
  data: unknown,
  clients: ClientsLike,
): Promise<{ action: 'focused' | 'opened'; url?: string }> {
  const alertId = alertIdOf((data as { alertId?: unknown } | null | undefined)?.alertId);
  const open = await clients.matchAll({ type: 'window', includeUncontrolled: true });
  for (const client of open) {
    if (client.focus) {
      await client.focus();
      client.postMessage({ type: 'open-alert', alertId });
      return { action: 'focused' };
    }
  }
  const url = alertPath(alertId);
  await clients.openWindow(url);
  return { action: 'opened', url };
}

export interface ResubscribeDeps {
  /** The old endpoint, so the server can drop it. Null when the browser kept none. */
  oldEndpoint: string | null;
  /** GET JSON from the API; null when the network or the server refused. Never throws. */
  get: (path: string) => Promise<unknown>;
  post: (path: string, body: unknown) => Promise<unknown>;
  subscribe: (applicationServerKey: Uint8Array) => Promise<PushSubscriptionJSON | null>;
}

/**
 * The browser rotated or dropped this subscription. Get a new one.
 *
 * A push subscription is not forever. Browsers rotate them, expire them, and
 * drop them when a push service key changes or storage is reclaimed — and the
 * only notice is the `pushsubscriptionchange` event. Without a handler the
 * subscription simply ceases to exist: the server keeps an endpoint that has
 * started returning 410, prunes it on the next send, and the device goes
 * quiet until somebody happens to open the app. On a phone that lives in the
 * app switcher for a week, that is a week of missed andons, with a granted
 * permission and a healthy-looking app.
 *
 * The public key is re-read from the server rather than cached, because the
 * case where it CHANGED is one of the reasons the event fires.
 */
export async function handleSubscriptionChange(deps: ResubscribeDeps): Promise<'resubscribed' | 'no-key' | 'refused'> {
  if (deps.oldEndpoint) await deps.post('/api/andon/push/unsubscribe', { endpoint: deps.oldEndpoint });
  const info = (await deps.get('/api/andon/notifications')) as { publicKey?: unknown } | null;
  const publicKey = typeof info?.publicKey === 'string' ? info.publicKey : null;
  if (!publicKey) return 'no-key';
  const fresh = await deps.subscribe(urlBase64ToUint8Array(publicKey));
  if (!fresh?.endpoint || !fresh.keys?.['p256dh'] || !fresh.keys?.['auth']) return 'refused';
  await deps.post('/api/andon/push/subscribe', {
    endpoint: fresh.endpoint,
    keys: { p256dh: fresh.keys['p256dh'], auth: fresh.keys['auth'] },
  });
  return 'resubscribed';
}

/**
 * VAPID keys travel as base64url; PushManager wants raw bytes.
 *
 * Duplicated in `pwa.ts` rather than shared, and that is not an oversight:
 * this module is pulled into the worker bundle, and an import from the app
 * would drag the app's module graph — react, the store, the API client — into
 * a worker that must stay small enough to start instantly on a phone waking
 * from sleep. Eight lines is the cheaper of the two costs.
 */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}
